
import React, { useState, useEffect } from 'react';
import { userTradeService, UserTrade } from '../../services/user.trade.service';
import { Search, Filter, Clock, Coins, ChevronRight, ChevronLeft, Loader2 } from 'lucide-react';
import { useToast } from '../../context/ToastContext';

const ITEMS_PER_PAGE = 10;

const Trades: React.FC = () => {
  const { showToast } = useToast();
  const [activeFilter, setActiveFilter] = useState<'ALL' | 'PROFIT' | 'LOSS' | 'PENDING'>('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [trades, setTrades] = useState<UserTrade[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await userTradeService.listTrades({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        outcome: activeFilter === 'ALL' ? undefined : activeFilter
      });
      if (response.success) {
        setTrades(response.data);
        if (response.pagination) {
          setTotalPages(response.pagination.pages);
          setTotalRecords(response.pagination.total);
        } else {
          setTotalPages(1);
          setTotalRecords(response.data.length);
        }
      }
    } catch (error) {
      showToast('Protocol Error: Failed to sync execution ledger', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, activeFilter]);

  const getStatusStyle = (outcome: string) => {
    switch (outcome) {
      case 'PROFIT':
        return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
      case 'LOSS':
        return 'bg-red-500/10 text-red-400 border-red-500/20';
      case 'PENDING':
        return 'bg-brand-primary/10 text-brand-primary border-brand-primary/20';
      default:
        return 'bg-brand-muted/10 text-brand-muted border-brand-border';
    }
  };

  const getAssetIcon = (asset: string) => {
    return <Coins size={16} className="text-brand-primary" />;
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      if (window.innerWidth < 768) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">

      {/* Header & Meta */}
      <section className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold text-white tracking-tight uppercase">Execution Ledger</h1>
          <p className="text-brand-muted text-xs font-bold uppercase tracking-widest opacity-70 flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-brand-primary rounded-full animate-pulse"></span>
            Real-time trade validation active
          </p>
        </div>

        {/* Filters */}
        <div className="flex bg-brand-card p-1 rounded-2xl border border-brand-border overflow-x-auto no-scrollbar">
          {(['ALL', 'PROFIT', 'LOSS', 'PENDING'] as const).map((filter) => (
            <button
              key={filter}
              onClick={() => { setActiveFilter(filter); setCurrentPage(1); }}
              className={`
                px-5 py-2 text-[10px] font-bold rounded-xl transition-all duration-300 uppercase tracking-widest whitespace-nowrap
                ${activeFilter === filter
                  ? 'bg-brand-primary text-black shadow-lg shadow-brand-primary/10'
                  : 'text-brand-muted hover:text-white hover:bg-white/5'}
              `}
            >
              {filter === 'ALL' ? 'Everything' : filter}
            </button>
          ))}
        </div>
      </section>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-brand-card border border-brand-border rounded-3xl overflow-hidden shadow-2xl relative min-h-[400px]">
        {isLoading && (
          <div className="absolute inset-0 bg-brand-dark/20 backdrop-blur-[1px] z-10 flex items-center justify-center">
            <Loader2 className="animate-spin text-brand-primary" size={24} />
          </div>
        )}
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-brand-dark/40 border-b border-brand-border">
              <th className="px-6 py-5 text-[10px] font-bold text-brand-muted uppercase tracking-[0.2em]">Transaction ID</th>
              <th className="px-6 py-5 text-[10px] font-bold text-brand-muted uppercase tracking-[0.2em]">Asset</th>
              <th className="px-6 py-5 text-[10px] font-bold text-brand-muted uppercase tracking-[0.2em]">Investment</th>
              <th className="px-6 py-5 text-[10px] font-bold text-brand-muted uppercase tracking-[0.2em]">Status</th>
              <th className="px-6 py-5 text-[10px] font-bold text-brand-muted uppercase tracking-[0.2em]">Payout</th>
              <th className="px-6 py-5 text-[10px] font-bold text-brand-muted uppercase tracking-[0.2em] text-right">Timestamp</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-border">
            {trades.map((trade) => (
              <tr key={trade.tradeId} className="hover:bg-brand-primary/5 transition-colors group cursor-default">
                <td className="px-6 py-5">
                  <span className="font-mono text-xs text-brand-muted group-hover:text-brand-primary transition-colors">
                    {trade.tradeId ? `${trade.tradeId.slice(0, 8)}...` : 'N/A'}
                  </span>
                </td>
                <td className="px-6 py-5">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-brand-dark border border-brand-border flex items-center justify-center">
                      {getAssetIcon(trade.asset)}
                    </div>
                    <span className="font-bold text-sm text-white">{trade.asset}</span>
                  </div>
                </td>
                <td className="px-6 py-5">
                  <span className="font-mono text-sm text-white">${parseFloat(trade.principal).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </td>
                <td className="px-6 py-5">
                  <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${getStatusStyle(trade.outcome)}`}>
                    {trade.outcome}
                  </span>
                </td>
                <td className="px-6 py-5">
                  <span className={`font-mono text-sm font-bold ${trade.outcome === 'PROFIT' ? 'text-emerald-500' : 'text-brand-muted opacity-50'}`}>
                    {trade.payout ? `+$${parseFloat(trade.payout).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—'}
                  </span>
                </td>
                <td className="px-6 py-5 text-right">
                  <span className="text-xs text-brand-muted">{new Date(trade.executedAt).toLocaleString()}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {trades.length === 0 && !isLoading && <EmptyState />}
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4 relative min-h-[200px]">
        {isLoading && (
          <div className="absolute inset-0 bg-brand-dark/20 backdrop-blur-[1px] z-10 flex items-center justify-center">
            <Loader2 className="animate-spin text-brand-primary" size={24} />
          </div>
        )}
        {trades.map((trade) => (
          <div key={trade.tradeId} className="bg-brand-card border border-brand-border rounded-2xl p-5 space-y-4 active:scale-[0.98] transition-transform">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-brand-dark border border-brand-border flex items-center justify-center">
                  {getAssetIcon(trade.asset)}
                </div>
                <div>
                  <h4 className="font-bold text-white uppercase tracking-tight">{trade.asset}</h4>
                  <p className="text-[10px] text-brand-muted font-mono">
                    {trade.tradeId ? `${trade.tradeId.slice(0, 12)}...` : 'N/A'}
                  </p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${getStatusStyle(trade.outcome)}`}>
                {trade.outcome}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 py-2 border-y border-brand-border/30">
              <div className="space-y-1">
                <p className="text-[9px] text-brand-muted uppercase font-bold tracking-widest">Investment</p>
                <p className="font-mono text-sm text-white">${parseFloat(trade.principal).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
              </div>
              <div className="space-y-1 text-right">
                <p className="text-[9px] text-brand-muted uppercase font-bold tracking-widest">Final Payout</p>
                <p className={`font-mono text-sm font-bold ${trade.outcome === 'PROFIT' ? 'text-emerald-500' : 'text-brand-muted'}`}>
                  {trade.payout ? `+$${parseFloat(trade.payout).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—'}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between text-[10px] text-brand-muted">
              <div className="flex items-center gap-1.5">
                <Clock size={12} />
                <span>{new Date(trade.executedAt).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center gap-1 text-brand-primary">
                <span className="font-bold uppercase tracking-widest">Details</span>
                <ChevronRight size={12} />
              </div>
            </div>
          </div>
        ))}

        {trades.length === 0 && !isLoading && <EmptyState />}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-brand-border/30">
          <p className="text-[10px] text-brand-muted font-bold uppercase tracking-[0.2em]">
            Showing <span className="text-white">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span> to <span className="text-white">{Math.min(currentPage * ITEMS_PER_PAGE, totalRecords)}</span> of <span className="text-white">{totalRecords}</span> executions
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || isLoading}
              className="p-2 rounded-xl bg-brand-card border border-brand-border text-brand-muted hover:text-brand-primary hover:border-brand-primary/30 disabled:opacity-30 disabled:pointer-events-none transition-all"
            >
              <ChevronLeft size={18} />
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                if (totalPages > 5) {
                  if (page !== 1 && page !== totalPages && Math.abs(page - currentPage) > 1) {
                    if (page === 2 || page === totalPages - 1) return <span key={page} className="text-brand-muted px-1">...</span>;
                    return null;
                  }
                }
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`
                      w-10 h-10 rounded-xl text-xs font-bold transition-all duration-300
                      ${currentPage === page
                        ? 'bg-brand-primary text-black shadow-lg shadow-brand-primary/20'
                        : 'text-brand-muted hover:text-white hover:bg-brand-border'}
                    `}
                  >
                    {page}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || isLoading}
              className="p-2 rounded-xl bg-brand-card border border-brand-border text-brand-muted hover:text-brand-primary hover:border-brand-primary/30 disabled:opacity-30 disabled:pointer-events-none transition-all"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const EmptyState = () => (
  <div className="py-20 flex flex-col items-center justify-center text-center px-6">
    <div className="w-16 h-16 rounded-full bg-brand-border/30 flex items-center justify-center text-brand-muted mb-4">
      <Filter size={32} />
    </div>
    <h3 className="text-white font-bold text-lg mb-1 uppercase tracking-tight">No match found</h3>
    <p className="text-brand-muted text-sm max-w-[240px]">We couldn't find any trades matching this execution protocol.</p>
  </div>
);

export default Trades;
